import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import type { ColDef, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { useBarrelLayouts } from "../hooks/useRollshopOperations";
import type {
	BarrelLayout,
	RollsetListItem,
} from "../models/RollshopOperations";
import type { RollPosition } from "./ChangeRollPositionModal";
import {
	ActionField,
	ActionIcon,
	RollsetInfoSection,
	RollsSection,
} from "./RollsetSections";

// Legacy hides the roll edit icons while the rollset is on the line; the procedures reject it anyway.
const ROLLS_LOCKED_STATUSES = new Set(["INUSE", "PREPAREDFORLINE"]);
const ROLLS_LOCKED_MESSAGE =
	"Not available while the rollset is In use or Prepared for line";

interface Props {
	rollset: RollsetListItem | null;
	disabled?: boolean;
	disabledReason?: string;
	onChangeRollPosition: (
		position: RollPosition,
		currentRollId: number | null,
		currentRollName: string | null,
	) => void;
	onChangeBarrel: () => void;
	onRemoveBarrel: () => void;
	onClose: () => void;
}

// Compact column set for the barrel layout grid, as the legacy IHBox shows it under the rollset.
const BARREL_LAYOUT_COLS: ColDef<BarrelLayout>[] = [
	{ field: "Position", headerName: "#", width: 50, sort: "asc" },
	{
		field: "PassName",
		headerName: "Pass Id",
		flex: 1,
		minWidth: 130,
	},
	{ field: "PassTypeName", headerName: "Type", width: 90 },
	{ field: "PassShapeName", headerName: "Shape", width: 100 },
	{
		field: "PassScheduledTonnage",
		headerName: "Sched Tons",
		width: 110,
		cellStyle: { textAlign: "right" },
		valueFormatter: (p: ValueFormatterParams<BarrelLayout>) =>
			p.value != null
				? Number(p.value).toLocaleString("en-US", {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					})
				: "",
	},
];

export const RollsetDetailPanel = ({
	rollset,
	disabled = false,
	disabledReason,
	onChangeRollPosition,
	onChangeBarrel,
	onRemoveBarrel,
	onClose,
}: Props) => {
	const barrelLayoutsQuery = useBarrelLayouts(rollset?.BarrelId ?? null);
	const barrelLayouts = useMemo(
		() => barrelLayoutsQuery.data ?? [],
		[barrelLayoutsQuery.data],
	);

	if (!rollset) {
		return (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: 3,
					color: "text.secondary",
				}}
			>
				<Typography variant="body2">Select a rollset to see details</Typography>
			</Box>
		);
	}

	const rollsLocked =
		disabled || ROLLS_LOCKED_STATUSES.has(rollset.StatusCode ?? "");
	const rollsLockedReason = disabled
		? (disabledReason ?? "")
		: ROLLS_LOCKED_MESSAGE;
	const lockedTitle = (title: string) =>
		disabled ? (disabledReason ?? "") : title;
	const barrelAction =
		rollset.BarrelId != null ? "Change barrel" : "Set barrel";

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<DetailHeader
				title={rollset.Name}
				isSaving={barrelLayoutsQuery.isFetching}
				onClose={onClose}
			/>
			<DetailBody>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "auto",
						display: "flex",
						flexDirection: "column",
						gap: 0.75,
					}}
				>
					{/* Read-only like the legacy: the op. status is changed from the Rollshop page. */}
					<RollsetInfoSection rollset={rollset} />

					<RollsSection
						rollset={rollset}
						includeExtraCommon
						locked={rollsLocked}
						lockedReason={rollsLockedReason}
						onChangeRoll={(p) =>
							onChangeRollPosition(
								p.position as RollPosition,
								p.rollId,
								p.rollName,
							)
						}
					/>

					<DetailSection title="Barrel" icon={<LayersOutlinedIcon />}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
							<ActionField
								label="Barrel"
								value={rollset.BarrelName ?? "None"}
								locked={disabled}
								onClick={onChangeBarrel}
								actions={
									<>
										<ActionIcon
											title={lockedTitle(barrelAction)}
											label={barrelAction}
											disabled={disabled}
											icon={<EditOutlinedIcon fontSize="small" />}
											onClick={onChangeBarrel}
										/>
										{rollset.BarrelId != null && (
											<ActionIcon
												title={lockedTitle("Remove barrel")}
												label="Remove barrel"
												disabled={disabled}
												icon={<DeleteOutlineIcon fontSize="small" />}
												onClick={onRemoveBarrel}
											/>
										)}
									</>
								}
							/>
							{rollset.BarrelId != null &&
								(barrelLayouts.length === 0 &&
								!barrelLayoutsQuery.isFetching ? (
									<Typography variant="body2" color="text.secondary">
										No passes assigned
									</Typography>
								) : (
									<AgGridReact<BarrelLayout>
										rowData={barrelLayouts}
										columnDefs={BARREL_LAYOUT_COLS}
										defaultColDef={{
											resizable: true,
											sortable: false,
										}}
										rowHeight={28}
										headerHeight={28}
										domLayout="autoHeight"
										suppressContextMenu
										suppressCellFocus
									/>
								))}
						</Box>
					</DetailSection>
				</Box>
			</DetailBody>
		</Box>
	);
};
